import { useState, useEffect, useCallback, useMemo } from 'react'
import useRouter from 'utils/useRouter'
import { useVestigioService } from 'hooks/useVestigioService'
import { useUsuarioService } from 'hooks/useUsuarioService'

export const useFormEditarVestigio = (props) => {
  const { route, idCaso, idVestigio } = props
  const router = useRouter()
  const vestigioService = useVestigioService()
  const usuarioService = useUsuarioService()

  // Estados dos valores do vestígio buscados na API
  const [vestigio, setVestigio] = useState(null)
  const [processandoVestigio, setProcessandoVestigio] = useState(false)
  const [arquivos, setArquivos] = useState([])
  const arquivosIniciais = useMemo(() => vestigio?.arqs?.map((arquivo) => arquivo.id) || [], [vestigio])

  //Estado que habilita a edição do lacre atual
  const [lacreAtualHabilitado, setLacreAtualHabilitado] = useState(true)

  // Busca do vestígio quando em modo de edição
  const fetchVestigio = useCallback(async () => {
    if (route.editar && idVestigio) {
      const response = await vestigioService.listarVestigioDetalhado(idVestigio, setProcessandoVestigio)
      setVestigio(response)
    }
  }, [idVestigio, route.editar])

  useEffect(() => {
    fetchVestigio()
  }, [fetchVestigio])

  useEffect(() => {
    if(vestigio?.lacre === vestigio?.lacreAcondicionamento){
        setLacreAtualHabilitado(false)
      }else{
        setLacreAtualHabilitado(true)
      }
  }, [vestigio])

  // Estados dos valores do formulário principal
  const [values, setValues] = useState({
    setRec: null,
    lacre: '',
    lacreAcondicionamento: '',
    numeroVestigioAssociado: '',
    tipoEmbalagem: 'SACO LACRE',
    outroTipoEmbalagem: '',
    corEmbalagem: 'TRANSPARENTE',
    outraCorEmbalagem: '',
    tipoFecho: 'ZIP LOCK',
    outroTipoFecho: '',
    logomarca: 'NAO',
    tipoLogomarca: '',
    tamanhoObjeto: '',
    endOrig: null,
    usuFix: null,
    dataFixacao: Date.now(),
    fixacaoInput: '',
    usuCol: null,
    dataColeta: Date.now(),
    coletaInput: '',
    usuAcond: null,
    acondicionamentoInput: '',
    dataAcondicionamento: Date.now(),
    usuTrans: null,
    transporteInput: '',
    dataTransporte: Date.now(),
    tiposGens: [],
    tiposEsps: [],
    quantidade: 1,
    descricao: '',
    arquivos: [],
  })

  // Campos que foram tocados (para exibir validações)
  const [touched, setTouched] = useState({})

  // Características dinâmicas para o cadastro de múltiplos vestígios
  const [caracteristicasVestigio, setCaracteristicasVestigio] = useState([
    {
      quantidade: 1,
      lacre: '',
      lacreAcondicionamento: '',
      numeroVestigioAssociado: '',
      tipoEmbalagem: 'SACO LACRE',
      outroTipoEmbalagem: '',
      tiposGens: [],
      tiposEsps: [],
      tamanhoObjeto: '',
      descricao: '',
      opcoesTiposVestigiosEspecificos: [],
    },
  ])

  // Estados auxiliares de dados das APIs
  const [usuarios, setUsuarios] = useState([])
  const [processandoUsuarios, setProcessandoUsuarios] = useState(false)
  const [tiposVestigios, setTiposVestigios] = useState([])
  const [processandoTiposVestigios, setProcessandoTiposVestigios] = useState(false)
  const [tiposVestigiosEspecificos, setTiposVestigiosEspecificos] = useState([])
  const [processandoTiposVestigiosEspecificos, setProcessandoTiposVestigiosEspecificos] = useState(false)
  const [opcoesTiposVestigiosEspecificosAtual, setOpcoesTiposVestigiosEspecificosAtual] = useState([])

  const [processandoOperacao, setProcessandoOperacao] = useState(false)
  const [openEspelhoConfirmacao, setOpenEspelhoConfirmacao] = useState(false)
  const [openDeletarVestigio, setOpenDeletarVestigio] = useState(false)

  // Carregar dados de apoio
  const fetchTiposVestigios = useCallback(async () => {
    const response = await vestigioService.listarTiposVestigiosAsync(setProcessandoTiposVestigios)
    setTiposVestigios(response || [])
  }, [])

  const fetchTiposVestigiosEspecificos = useCallback(async () => {
    const response = await vestigioService.listarTiposVestigiosEspecificosAsync(setProcessandoTiposVestigiosEspecificos)
    setTiposVestigiosEspecificos(response || [])
  }, [])

  const fetchUsuarios = useCallback(async () => {
    const response = await usuarioService.listarUsuariosAsync(setProcessandoUsuarios)
    setUsuarios(response || [])
  }, [])

  useEffect(() => {
    fetchTiposVestigios()
    fetchTiposVestigiosEspecificos()
    fetchUsuarios()
  }, [fetchTiposVestigios, fetchTiposVestigiosEspecificos, fetchUsuarios])

  // Inicializar formulário se estiver em modo de edição
  useEffect(() => {
    if (vestigio && vestigio.id) {
      setValues({
        setRec: vestigio.setRec || null,
        lacre: vestigio.lacre || '',
        lacreAcondicionamento: vestigio.lacreAcondicionamento || '',
        numeroVestigioAssociado: vestigio.numeroVestigioAssociado || '',
        tipoEmbalagem: vestigio.tipoEmbalagem || 'SACO LACRE',
        outroTipoEmbalagem: vestigio.outroTipoEmbalagem || '',
        corEmbalagem: vestigio.corEmbalagem || 'TRANSPARENTE',
        outraCorEmbalagem: vestigio.outraCorEmbalagem || '',
        tipoFecho: vestigio.tipoFecho || 'ZIP LOCK',
        outroTipoFecho: vestigio.outroTipoFecho || '',
        logomarca: vestigio.logomarca ? vestigio.logomarca : 'NAO',
        tipoLogomarca: vestigio.tipoLogomarca || '',
        tamanhoObjeto: vestigio.tamanhoObjeto || '',
        endOrig: vestigio.endOrig || null,
        usuFix: vestigio.usuFix || null,
        dataFixacao: vestigio.dataFixacao || Date.now(),
        fixacaoInput: vestigio.fixacaoInput || '',
        usuCol: vestigio.usuCol || null,
        dataColeta: vestigio.dataColeta || Date.now(),
        coletaInput: vestigio.coletaInput || '',
        usuAcond: vestigio.usuAcond || null,
        acondicionamentoInput: vestigio.acondicionamentoInput || '',
        dataAcondicionamento: vestigio.dataAcondicionamento || Date.now(),
        usuTrans: vestigio.usuTrans || null,
        transporteInput: vestigio.transporteInput || '',
        dataTransporte: vestigio.dataTransporte || Date.now(),
        tiposGens: vestigio.tiposGens || [],
        tiposEsps: vestigio.tiposEsps || [],
        quantidade: vestigio.quantidade || 1,
        descricao: vestigio.descricao || '',
        arquivos: vestigio.arqs ? vestigio.arqs.map((arq) => arq.id) : [],
      })

      // Atualizar naturezas específicas com base nas genéricas
      const opcoesTiposEspecificos = new Set()
      const gens = vestigio.tiposGens || []
      tiposVestigiosEspecificos.forEach((tipoEspecifico) => {
        tipoEspecifico.tiposGens?.forEach((tipoGenerico) => {
          gens.forEach((val) => {
            if (val.identificador === tipoGenerico.identificador) {
              opcoesTiposEspecificos.add(tipoEspecifico)
            }
          })
        })
      })
      setOpcoesTiposVestigiosEspecificosAtual([...opcoesTiposEspecificos])
    }
  }, [vestigio, tiposVestigiosEspecificos])

  // Vinculo automático de arquivos
  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      arquivos: arquivos || [],
    }))
  }, [arquivos])

  // Preenchimento automático de cadeia de custódia a partir do fixador
  useEffect(() => {
    if (values.usuFix) {
      setValues((prev) => {
        const usuCol = prev.usuCol === null ? prev.usuFix : prev.usuCol
        const usuAcond = prev.usuAcond === null ? prev.usuFix : prev.usuAcond
        const usuTrans = prev.usuTrans === null ? prev.usuFix : prev.usuTrans
        return {
          ...prev,
          usuCol,
          usuAcond,
          usuTrans,
        }
      })
    }
  }, [values.usuFix])

  // Handlers genéricos de mudança
  const handleChange = useCallback((e) => {
    const { name, value } = e.target
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  const handleChangeWithModification = useCallback((e, modificationFn) => {
    const { name, value } = e.target
    setValues((prev) => ({
      ...prev,
      [name]: modificationFn(value),
    }))
  }, [])

  const handleChangeNewValueByNameProperty = useCallback((name, newValue) => {
    setValues((prev) => ({
      ...prev,
      [name]: newValue,
    }))
  }, [])

  const handleChangeCampoData = useCallback((name, date) => {
    setValues((prev) => ({
      ...prev,
      [name]: date,
    }))
  }, [])

  const handleBlur = useCallback((name) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }))
  }, [])

  // Handler especializado para alteração da natureza genérica
  const handleChangeTipoVestigioGenerica = useCallback((event, newValue) => {
    handleChangeNewValueByNameProperty('tiposGens', newValue)
    const opcoesTiposEspecificos = new Set()

    tiposVestigiosEspecificos.forEach((tipoEspecifico) => {
      tipoEspecifico.tiposGens?.forEach((tipoGenerico) => {
        newValue.forEach((novoValor) => {
          if (novoValor.identificador === tipoGenerico.identificador) {
            opcoesTiposEspecificos.add(tipoEspecifico)
          }
        })
      })
    })

    setOpcoesTiposVestigiosEspecificosAtual([...opcoesTiposEspecificos])
  }, [tiposVestigiosEspecificos, handleChangeNewValueByNameProperty])

  // Manipulação de Características Dinâmicas (Adição Múltipla)
  const adicionarCaracteristica = useCallback(() => {
    setCaracteristicasVestigio((prev) => [
      ...prev,
      {
        quantidade: 1,
        lacre: '',
        lacreAcondicionamento: '',
        numeroVestigioAssociado: '',
        tipoEmbalagem: 'SACO LACRE',
        outroTipoEmbalagem: '',
        tiposGens: [],
        tiposEsps: [],
        tamanhoObjeto: '',
        descricao: '',
        opcoesTiposVestigiosEspecificos: [],
      },
    ])
  }, [])

  const removerCaracteristica = useCallback((index) => {
    setCaracteristicasVestigio((prev) => {
      if (prev.length > 1) {
        const copy = [...prev]
        copy.splice(index, 1)
        return copy
      }
      return prev
    })
  }, [])

  const atualizarCaracteristica = useCallback((index, campo, valor) => {
    setCaracteristicasVestigio((prev) => {
      const copy = [...prev]
      const limitesCaracteres = {
        descricao: 8000,
        lacre: 50,
        numeroVestigioAssociado: 50,
        quantidade: 5,
      }

      let val = valor
      if (limitesCaracteres[campo] && val.length > limitesCaracteres[campo]) {
        val = val.slice(0, limitesCaracteres[campo])
      }

      copy[index][campo] = val

      if (campo === 'tiposGens') {
        const opcoesTiposEspecificos = new Set()
        tiposVestigiosEspecificos.forEach((tipoEspecifico) => {
          tipoEspecifico.tiposGens?.forEach((tipoGenerico) => {
            val.forEach((novoValor) => {
              if (novoValor.identificador === tipoGenerico.identificador) {
                opcoesTiposEspecificos.add(tipoEspecifico)
              }
            })
          })
        })
        copy[index]['opcoesTiposVestigiosEspecificos'] = [...opcoesTiposEspecificos]
      }

      return copy
    })
  }, [tiposVestigiosEspecificos])

  // Validação em tempo real
  const errors = useMemo(() => {
    const err = {}
    if (!values.setRec) err.setRec = 'Setor de entrada é obrigatório'
    if (!values.endOrig) err.endOrig = 'Local de coleta é obrigatório'
    if (!values.usuFix) err.usuFix = 'Responsável pela fixação é obrigatório'
    if (!values.dataFixacao) err.dataFixacao = 'Data de fixação é obrigatória'
    if (!values.usuCol) err.usuCol = 'Responsável pela coleta é obrigatório'
    if (!values.dataColeta) err.dataColeta = 'Data de coleta é obrigatória'
    if (!values.usuAcond) err.usuAcond = 'Responsável pelo acondicionamento é obrigatório'
    if (!values.dataAcondicionamento) err.dataAcondicionamento = 'Data de acondicionamento é obrigatória'
    if (!values.usuTrans) err.usuTrans = 'Responsável pelo transporte é obrigatório'
    if (!values.dataTransporte) err.dataTransporte = 'Data de transporte é obrigatória'

    if (route.editar) {
      if (!values.lacre?.trim()) err.lacre = 'Número do lacre atual é obrigatório'
      if (!values.lacreAcondicionamento?.trim()) err.lacreAcondicionamento = 'Número do lacre inicial é obrigatório'
      if (!values.tipoEmbalagem) err.tipoEmbalagem = 'Tipo da embalagem é obrigatório'
      if (!values.tiposGens || values.tiposGens.length === 0) err.tiposGens = 'Natureza genérica é obrigatória'
      if (!values.tiposEsps || values.tiposEsps.length === 0) err.tiposEsps = 'Natureza específica é obrigatória'
      if (!values.descricao?.trim()) err.descricao = 'Descrição do vestígio é obrigatória'
      if (!values.quantidade) err.quantidade = 'Unidades é obrigatória'
    }

    return err
  }, [values, route.editar])

  const isFormularioInvalido = useMemo(() => {
    const hasBasicErrors = Object.keys(errors).length > 0
    if (hasBasicErrors) return true

    if (!route.editar) {
      // Validar características dinâmicas
      let dynamicInvalid = false
      caracteristicasVestigio.forEach((char) => {
        if (
          !char.quantidade ||
          !char.lacre?.trim() ||
          !char.tipoEmbalagem ||
          !char.descricao?.trim() ||
          !char.tiposEsps ||
          char.tiposEsps.length === 0 ||
          !char.tiposGens ||
          char.tiposGens.length === 0
        ) {
          dynamicInvalid = true
        }
      })
      return dynamicInvalid
    }

    return false
  }, [errors, route.editar, caracteristicasVestigio])

  // Ações de Submissão
  const handleConfirmarOperacao = useCallback(async () => {
    if (isFormularioInvalido) return

    if (route.editar && vestigio) {
      await vestigioService.editarVestigio(vestigio.id, values, setProcessandoOperacao, lacreAtualHabilitado)
      router.history.push(`/casos/caso/vestigio/detalhar/${vestigio.id}`)
    } else {
      setProcessandoOperacao(true)
      const dados = {
        ...values,
        idCaso,
        caracteristicasVestigio,
      }
      await vestigioService.cadastrarMultiplosVestigios(dados, idCaso)
      setProcessandoOperacao(false)
      router.history.push(`/casos/caso/detalhar/${idCaso}`)
    }
  }, [isFormularioInvalido, route.editar, values, caracteristicasVestigio, idCaso, vestigio, router])

  const handleClickConfirmDeletarVestigio = useCallback(async () => {
    if (vestigio) {
      await vestigioService.deletarVestigio(vestigio.id, setProcessandoOperacao)
      router.history.push(`/casos/caso/detalhar/${vestigio.casos[0].id}`)
    }
  }, [vestigio, router])

  return {
    vestigio,
    processandoVestigio,
    arquivos,
    setArquivos,
    arquivosIniciais,
    values,
    touched,
    errors,
    isFormularioInvalido,
    caracteristicasVestigio,
    usuarios,
    processandoUsuarios,
    tiposVestigios,
    processandoTiposVestigios,
    processandoTiposVestigiosEspecificos,
    opcoesTiposVestigiosEspecificosAtual,
    processandoOperacao,
    openEspelhoConfirmacao,
    setOpenEspelhoConfirmacao,
    openDeletarVestigio,
    setOpenDeletarVestigio,
    lacreAtualHabilitado,

    // Handlers
    handleChange,
    handleChangeWithModification,
    handleChangeNewValueByNameProperty,
    handleChangeCampoData,
    handleBlur,
    handleChangeTipoVestigioGenerica,

    // Características
    adicionarCaracteristica,
    removerCaracteristica,
    atualizarCaracteristica,

    // Operações
    handleConfirmarOperacao,
    handleClickConfirmDeletarVestigio,
  }
}
